import React, { useState, useEffect } from 'react';
import { ticketService } from '../services/ticketService';
import { Ticket } from '../types/ticket';
import { Link } from 'react-router-dom';
import { useFeedbackModalStore } from '../stores/useFeedbackModalStore';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { Button, Card, Badge, Textarea } from '../components/ui';
import { APP_HOME } from '../utils/segment';

// Page-load entrance variants — numeric durations mirror --snapfy-duration-base (300ms) /
// --snapfy-duration-slow (500ms); easing mirrors --snapfy-ease-standard.
const headingVariants: Variants = {
    hidden: { opacity: 0, y: -12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
} as const;

const listVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.15 } },
} as const;

const UserTickets: React.FC = () => {
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const { openModal: openFeedbackModal } = useFeedbackModalStore();
    const prefersReducedMotion = useReducedMotion();

    const loadTickets = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await ticketService.getMyTickets();
            setTickets(response.data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erro ao carregar tickets');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadTickets();
    }, []);

    const openTicketDetail = async (ticketId: number) => {
        try {
            const ticket = await ticketService.getTicket(ticketId);
            setSelectedTicket(ticket);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erro ao carregar ticket');
        }
    };

    const handleReply = async () => {
        if (!selectedTicket || !replyMessage.trim()) return;

        setIsSending(true);
        try {
            const updatedTicket = await ticketService.addMessage(selectedTicket.id, replyMessage);
            setSelectedTicket(updatedTicket);
            setReplyMessage('');
            loadTickets();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erro ao enviar mensagem');
        } finally {
            setIsSending(false);
        }
    };

    const getStatusBadgeVariant = (status: string): 'info' | 'warning' | 'success' | 'error' | 'count' => {
        switch (status) {
            case 'open': return 'info';
            case 'in_progress': return 'warning';
            case 'waiting_user': return 'info';
            case 'waiting_support': return 'warning';
            case 'closed': return 'count';
            default: return 'count';
        }
    };

    const getPriorityBadgeVariant = (priority: string): 'error' | 'warning' | 'success' | 'info' | 'count' => {
        switch (priority) {
            case 'high': return 'error';
            case 'medium': return 'warning';
            case 'low': return 'success';
            default: return 'count';
        }
    };

    // Reduced-motion guard: flatten entrance for users who prefer it
    const resolvedHeading = prefersReducedMotion
        ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0, transition: { duration: 0 } } } as Variants
        : headingVariants;
    const resolvedList = prefersReducedMotion
        ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0, transition: { duration: 0 } } } as Variants
        : listVariants;

    return (
        <div className="min-h-screen bg-snapfy-bg p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <motion.div
                    className="mb-8 flex justify-between items-start"
                    variants={resolvedHeading}
                    initial="hidden"
                    animate="visible"
                >
                    <div>
                        <Link to={APP_HOME} className="text-snapfy-text-secondary hover:text-snapfy-text-primary text-sm mb-4 inline-flex items-center gap-2 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Voltar
                        </Link>
                        <h1 className="text-2xl font-bold text-white">Suporte &amp; Feedback</h1>
                        <p className="text-snapfy-text-secondary mt-1">Envie sugestões, reporte bugs ou acompanhe suas solicitações</p>
                    </div>
                    <Button
                        variant="solid"
                        onClick={openFeedbackModal}
                        leadingIcon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        }
                    >
                        Novo Ticket
                    </Button>
                </motion.div>

                {/* Error Alert */}
                {error && (
                    <div className="mb-6 p-4 bg-snapfy-error/10 border border-snapfy-error/50 rounded-lg text-snapfy-error flex justify-between items-center">
                        <span>{error}</span>
                        <button onClick={() => setError('')} className="text-snapfy-error hover:opacity-70 transition-opacity focus-visible:outline-none focus-visible:ring-snapfy-focus rounded">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}

                {/* Tickets List */}
                <motion.div
                    variants={resolvedList}
                    initial="hidden"
                    animate="visible"
                >
                    <Card className="p-0 overflow-hidden">
                        {isLoading ? (
                            <div className="p-12 text-center">
                                <svg className="animate-spin h-8 w-8 text-snapfy-primary mx-auto" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        ) : tickets.length === 0 ? (
                            <div className="p-12 text-center">
                                <div className="w-16 h-16 bg-snapfy-inner rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-snapfy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                </div>
                                <p className="text-snapfy-text-secondary">Você ainda não enviou nenhum ticket</p>
                                <Button
                                    variant="solid"
                                    onClick={openFeedbackModal}
                                    className="mt-4"
                                    leadingIcon={
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                    }
                                >
                                    Criar Primeiro Ticket
                                </Button>
                            </div>
                        ) : (
                            <div className="divide-y divide-snapfy-border">
                                {tickets.map((ticket) => (
                                    <button
                                        key={ticket.id}
                                        onClick={() => openTicketDetail(ticket.id)}
                                        className="w-full p-4 text-left hover:bg-snapfy-surface-hover/50 transition-colors focus-visible:outline-none focus-visible:ring-snapfy-focus"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-mono text-snapfy-text-secondary">
                                                        {ticket.ticketNumber || `#${ticket.id}`}
                                                    </span>
                                                    <Badge variant={getStatusBadgeVariant(ticket.status)}>
                                                        {ticket.statusLabel}
                                                    </Badge>
                                                </div>
                                                <h3 className="text-white font-medium truncate">{ticket.subject}</h3>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <Badge variant={getPriorityBadgeVariant(ticket.priority)}>
                                                        {ticket.priorityLabel}
                                                    </Badge>
                                                    <span className="text-xs text-snapfy-text-secondary">
                                                        {new Date(ticket.createdAt).toLocaleDateString('pt-BR')}
                                                    </span>
                                                    {ticket.messagesCount && ticket.messagesCount > 1 && (
                                                        <span className="text-xs text-snapfy-text-secondary flex items-center gap-1">
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                            </svg>
                                                            {ticket.messagesCount} mensagens
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <svg className="w-5 h-5 text-snapfy-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </Card>
                </motion.div>
            </div>

            {/* Ticket Detail Modal — native overlay kept: holds unsaved reply state (Textarea value),
                no auto-close on overlay click/Esc by original design; restyle via tokens only */}
            {selectedTicket && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-snapfy-surface rounded-card shadow-card border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-snapfy-border flex justify-between items-start">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-mono text-snapfy-text-secondary">
                                        {selectedTicket.ticketNumber || `#${selectedTicket.id}`}
                                    </span>
                                </div>
                                <h2 className="text-xl font-bold text-white">{selectedTicket.subject}</h2>
                                <div className="flex gap-2 mt-2">
                                    <Badge variant={getStatusBadgeVariant(selectedTicket.status)}>
                                        {selectedTicket.statusLabel}
                                    </Badge>
                                    <Badge variant={getPriorityBadgeVariant(selectedTicket.priority)}>
                                        {selectedTicket.priorityLabel}
                                    </Badge>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedTicket(null)}
                                className="text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors focus-visible:outline-none focus-visible:ring-snapfy-focus rounded"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {selectedTicket.messages?.map((msg) => (
                                <div
                                    key={msg.id}
                                    className={`flex ${msg.isFromAdmin ? 'justify-start' : 'justify-end'}`}
                                >
                                    <div className={`max-w-[80%] rounded-card p-4 border border-white/10 ${msg.isFromAdmin ? 'bg-snapfy-primary-10' : 'bg-snapfy-inner'}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`text-sm font-medium ${msg.isFromAdmin ? 'text-snapfy-accent-soft' : 'text-white'}`}>
                                                {msg.isFromAdmin ? 'Suporte' : 'Você'}
                                            </span>
                                            {msg.isFromAdmin && (
                                                <Badge variant="info">Equipe</Badge>
                                            )}
                                        </div>
                                        <p className="text-snapfy-text-secondary whitespace-pre-wrap">{msg.message}</p>
                                        {msg.hasScreenshot && (
                                            <button
                                                onClick={() => ticketService.downloadScreenshot(msg.id)}
                                                className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-snapfy-inner hover:bg-snapfy-surface text-snapfy-text-secondary hover:text-snapfy-text-primary rounded-card text-sm transition-colors focus-visible:outline-none focus-visible:ring-snapfy-focus"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                Baixar Screenshot
                                            </button>
                                        )}
                                        <div className="text-xs text-snapfy-text-secondary mt-2">
                                            {new Date(msg.createdAt).toLocaleString('pt-BR')}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Reply Form */}
                        {selectedTicket.isOpen ? (
                            <div className="p-6 border-t border-snapfy-border">
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <Textarea
                                            value={replyMessage}
                                            onChange={(e) => setReplyMessage(e.target.value)}
                                            placeholder="Digite sua mensagem..."
                                            rows={3}
                                            className="resize-none"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end mt-4">
                                    <Button
                                        variant="solid"
                                        onClick={handleReply}
                                        disabled={!replyMessage.trim() || isSending}
                                        isLoading={isSending}
                                    >
                                        {isSending ? 'Enviando...' : 'Enviar Mensagem'}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="p-6 border-t border-snapfy-border bg-snapfy-inner/50">
                                <div className="text-center text-snapfy-text-secondary">
                                    <span>Este ticket foi encerrado</span>
                                    {selectedTicket.closedAt && (
                                        <span> em {new Date(selectedTicket.closedAt).toLocaleString('pt-BR')}</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}


        </div>
    );
};

export default UserTickets;
